/** Pakistani mobile: 03XXXXXXXXX (11 digits). */
export const PK_MOBILE_REGEX = /^03\d{9}$/;

export const PK_MOBILE_ERROR =
  'Enter a valid Pakistani mobile number (e.g. 03001234567).';

/** Digits only; converts 92300xxxxxxx → 0300xxxxxxx. */
export function normalizePkMobile(mobile: string): string {
  let digits = mobile.replace(/\D/g, '');
  if (digits.startsWith('92') && digits.length === 12) {
    digits = `0${digits.slice(2)}`;
  }
  return digits;
}

/** Restrict input while typing (digits only, max 11). */
export function sanitizePkMobileInput(value: string): string {
  return value.replace(/\D/g, '').slice(0, 11);
}

/**
 * Returns an error message, or '' if valid.
 * @param required — when true, empty is an error
 */
export function validatePkMobile(
  mobile: string,
  options: { required?: boolean } = {},
): string {
  return validatePhone(mobile, 'PK', options);
}

export function isValidPkMobile(mobile: string): boolean {
  return PK_MOBILE_REGEX.test(normalizePkMobile(mobile));
}

/** Digits-only sanitize with country-aware max length. */
export function sanitizePhoneInput(value: string, countryCode?: string | null): string {
  const digits = value.replace(/\D/g, '');
  const max = countryCode === 'PK' ? 11 : countryCode === 'US' ? 11 : 15;
  return digits.slice(0, max);
}

export function normalizePhone(mobile: string, countryCode?: string | null): string {
  const code = countryCode || 'PK';
  if (code === 'PK') return normalizePkMobile(mobile);
  let digits = mobile.replace(/\D/g, '');
  if (code === 'US' || code === 'CA') {
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    return digits;
  }
  if (code === 'GB') {
    if (digits.startsWith('44') && digits.length > 10) digits = `0${digits.slice(2)}`;
    return digits;
  }
  // EU / other: keep digits; strip leading 00
  if (digits.startsWith('00')) digits = digits.slice(2);
  return digits;
}

export function validatePhone(
  mobile: string,
  countryCode?: string | null,
  options: { required?: boolean } = {},
): string {
  const { required = false } = options;
  const code = countryCode || 'PK';
  const normalized = normalizePhone(mobile, code);

  if (!normalized) {
    return required ? 'Mobile number is required.' : '';
  }

  if (code === 'PK') {
    if (!PK_MOBILE_REGEX.test(normalized)) return PK_MOBILE_ERROR;
    return '';
  }

  if (code === 'US') {
    if (!/^\d{10}$/.test(normalized)) {
      return 'Enter a valid US phone number (10 digits).';
    }
    return '';
  }

  if (code === 'GB') {
    if (!/^0\d{9,10}$/.test(normalized) && !/^\d{10,11}$/.test(normalized)) {
      return 'Enter a valid UK phone number.';
    }
    return '';
  }

  // EU / fallback: 8–15 digits
  if (normalized.length < 8 || normalized.length > 15) {
    return 'Enter a valid phone number.';
  }
  return '';
}
